import React, { useState, useEffect, useCallback } from 'react';
import {
  PlanNotFoundError,
  UnauthorizedError,
  getPlan,
  exportPlan,
} from '../services/api';

/**
 * FinancialPlanPanel — the Financial Plan as a genuine RHS TAB (live-test
 * UI issue 2): Business Plan and SBA Package render as integrated tabs, but
 * the financial plan was only reachable via a full-screen PlanView takeover.
 * This embedded panel gives it the same tab-native treatment: headline
 * figures + xlsx/csv downloads, with the full-screen view one click away for
 * the detailed month-by-month tables.
 *
 * ZERO-HALLUCINATION: every figure is a single direct read from the engine
 * output — no arithmetic here (same contract as DataPanel).
 */

function fmtMoney(value) {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

export default function FinancialPlanPanel({
  sessionId,
  refreshSignal,
  onOpenFull,
  embedded = false,
}) {
  const [plan, setPlan] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | stale | error
  const [exporting, setExporting] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const p = await getPlan(sessionId);
      setPlan(p);
      setStatus(p && p.status === 'coming_soon' ? 'error' : 'ready');
    } catch (err) {
      if (err instanceof UnauthorizedError) return;
      setStatus(err instanceof PlanNotFoundError ? 'stale' : 'error');
    }
  }, [sessionId]);

  useEffect(() => { refresh(); }, [refresh, refreshSignal]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      await exportPlan(sessionId, format);
    } finally {
      setExporting(null);
    }
  };

  if (status === 'loading') {
    return <p className="bp-panel-status">Loading your financial plan…</p>;
  }
  if (status === 'stale') {
    return (
      <p className="bp-panel-status">
        Your financial plan needs to be recalculated — your answers changed
        since it was generated. Ask Eliza to run the numbers again.
      </p>
    );
  }
  if (status === 'error' || !plan) {
    return <p className="bp-panel-status">No financial plan is available yet.</p>;
  }

  const sc = plan.startup_costs || {};
  const be = plan.break_even || {};
  const rows = [
    ['Total Capital Required', fmtMoney(sc.total_capital_required)],
    ['Break-Even Revenue (Operating)', fmtMoney(be.break_even_revenue_operating)],
    ['Break-Even Revenue (Incl. Debt Service)',
     fmtMoney(be.break_even_revenue_with_debt_service)],
    plan.dscr != null && ['Year-1 DSCR', String(plan.dscr)],
  ].filter(Boolean);

  return (
    <div className="data-panel-body">
      <section className="data-panel-section">
        <h3 className="data-panel-section-title">Financial Plan</h3>
        <div className="data-panel-section-body">
          {rows.map(([label, value]) => (
            <div key={label} className="data-panel-row">
              <span className="data-panel-label">{label}</span>
              <span className="data-panel-value">{value}</span>
            </div>
          ))}
        </div>
      </section>
      <div className="bp-panel-downloads">
        <button className="bp-download-btn" disabled={exporting !== null}
                onClick={() => handleExport('excel')}>
          {exporting === 'excel' ? 'Preparing…' : '⬇ Excel Workbook'}
        </button>
        <button className="bp-download-btn" disabled={exporting !== null}
                onClick={() => handleExport('csv')}>
          {exporting === 'csv' ? 'Preparing…' : '⬇ CSV'}
        </button>
        {onOpenFull && (
          <button className="bp-download-btn" onClick={onOpenFull}>
            Open Full Plan View
          </button>
        )}
      </div>
    </div>
  );
}
